import { useRef } from "react";
import { useEditorState } from "@tiptap/react";
import type { Editor } from "@tiptap/react";
import type { Selection } from "@tiptap/pm/state";
import { HugeiconsIcon, type IconSvgElement } from "@hugeicons/react";
import {
  TextBoldIcon,
  TextItalicIcon,
  TextUnderlineIcon,
  Link06Icon,
  File02Icon,
  LeftToRightListBulletIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { cx } from "shared/utils";

interface ToolbarButtonProps {
  icon: IconSvgElement;
  onClick: () => void;
  active?: boolean;
  title: string;
  buttonRef?: React.RefObject<HTMLButtonElement | null>;
}

const ToolbarButton = ({
  icon,
  onClick,
  active,
  title,
  buttonRef
}: ToolbarButtonProps) => {
  const strokeWidth = active ? 2 : 1.5;
  return (
    <button
      ref={buttonRef as React.RefObject<HTMLButtonElement>}
      type="button"
      title={title}
      onMouseDown={(e) => {
        e.preventDefault();
        onClick();
      }}
      className={cx(
        "flex size-8 items-center justify-center rounded transition-colors hover:bg-alpha-40",
        active ? "text-primary-500" : "text-content-400"
      )}
    >
      <HugeiconsIcon icon={icon} size={18} strokeWidth={strokeWidth} />
    </button>
  );
};

interface NoteToolbarProps {
  editor: Editor | null;
  onFileUpload: (files: FileList) => void;
  onOpenLinkPopover: (args: {
    anchorElement: HTMLElement | null;
    initialText: string;
    initialUrl: string;
    savedSelection: Selection | null;
    savedText: string;
    startInEditMode: boolean;
  }) => void;
}

export const NoteToolbar = ({
  editor,
  onFileUpload,
  onOpenLinkPopover
}: NoteToolbarProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const linkButtonRef = useRef<HTMLButtonElement>(null);

  const editorState = useEditorState({
    editor,
    selector: (ctx) => ({
      isBold: ctx.editor?.isActive("bold") ?? false,
      isItalic: ctx.editor?.isActive("italic") ?? false,
      isUnderline: ctx.editor?.isActive("underline") ?? false,
      isBulletList: ctx.editor?.isActive("bulletList") ?? false,
      isLink: ctx.editor?.isActive("link") ?? false
    })
  });

  const handleLinkButtonClick = () => {
    if (!linkButtonRef.current) return;
    let selection: Selection | null = null;
    let selectedText = "";
    let selectedLinkUrl = "";
    if (editor) {
      selection = editor.state.selection;
      const { from, to } = editor.state.selection;
      selectedText = editor.state.doc.textBetween(from, to, "");
      selectedLinkUrl = editor.getAttributes("link").href ?? "";
    }
    onOpenLinkPopover({
      anchorElement: linkButtonRef.current,
      initialText: selectedText,
      initialUrl: selectedLinkUrl,
      savedSelection: selection,
      savedText: selectedText,
      startInEditMode: true
    });
  };

  return (
    <div className="p-4">
      <div className="flex items-center gap-0.5">
        <ToolbarButton
          icon={TextBoldIcon}
          title="Bold"
          active={editorState?.isBold}
          onClick={() => editor?.chain().focus().toggleBold().run()}
        />
        <ToolbarButton
          icon={TextItalicIcon}
          title="Italic"
          active={editorState?.isItalic}
          onClick={() => editor?.chain().focus().toggleItalic().run()}
        />
        <ToolbarButton
          icon={TextUnderlineIcon}
          title="Underline"
          active={editorState?.isUnderline}
          onClick={() => editor?.chain().focus().toggleUnderline().run()}
        />
        <ToolbarButton
          icon={LeftToRightListBulletIcon}
          title="Bullet list"
          active={editorState?.isBulletList}
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
        />

        <div className="mx-1.5 h-4 w-px bg-alpha-200" />

        <ToolbarButton
          icon={Link06Icon}
          title="Add link"
          active={editorState?.isLink}
          buttonRef={linkButtonRef}
          onClick={handleLinkButtonClick}
        />
        <ToolbarButton
          icon={File02Icon}
          title="Attach file"
          onClick={() => fileInputRef.current?.click()}
        />

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,.pdf"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.length) {
              onFileUpload(e.target.files);
              e.target.value = "";
            }
          }}
        />
      </div>
    </div>
  );
};
